import api from "api";
import { Moment } from "moment";
import { useCallback, useEffect, useState } from "react";
import { ClipType, TopAuthorType } from "types";
import "./top-authors.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "redux/reducers";
import { toast } from "react-toastify";

function TopAuthors({
    projectId,
    dateFrom,
    dateTo,
    streamIds,
    onAddToInfluencerDiscovery,
}: {
    projectId: number;
    dateFrom: Moment;
    dateTo: Moment;
    streamIds: number[];
    onAddToInfluencerDiscovery: (author: { authorId: string; authorName: string; streamId: number }) => void;
}) {
    const params: any = useParams();
    const dispatch = useDispatch();
    const [topAuthors, setTopAuthors] = useState<TopAuthorType[]>([]);
    const [filterId, setFilterId] = useState<number>(1);
    const [menuOpen, setMenuOpen] = useState<string>("");
    const [showAll, setShowAll] = useState(false);

    const state = useSelector((state: any) => state.author.active);

    useEffect(() => {
        getTopAuthors();
        dispatch(actions.author.unset());
    }, [state]);

    const getStream = (streamId: number) => {
        return (
            {
                7: "x-twitter",
                8: "facebook",
                9: "instagram",
                10: "youtube",
                11: "linkedin",
            }[streamId] || ""
        );
    };

    const openMenu = (id: string, e: any) => {
        e.stopPropagation();
        setMenuOpen(id);
    };

    const addToInfluencerDiscovery = async (authorId: string, authorName: string, streamId: number) => {
        await api.influencerDiscovery.addToInfluencerDiscovery(authorId, params.projectId, authorName, streamId).then(() => toast.success("Influencer added"));
        onAddToInfluencerDiscovery({ authorId, authorName, streamId });
    };

    const getTopAuthors = useCallback(async () => {
        const data = await api.topAuthors.getAll(projectId, dateFrom, dateTo, streamIds);
        if (data.length) {
            setTopAuthors(data);
        } else {
            setTopAuthors([]);
        }
    }, [projectId, dateFrom, dateTo, streamIds]);

    const viewPosts = async (authorId: string, e: any) => {
        e.stopPropagation();
        setMenuOpen("");
        dispatch(
            actions.clipTray.set({
                loading: true,
                clips: [],
                open: true,
            })
        );
        const clips: ClipType[] = await api.clip.getAll(projectId, dateFrom, dateTo, streamIds.join(","));
        dispatch(
            actions.clipTray.set({
                loading: false,
                clips: clips.filter((x) => x.AuthorId === authorId),
                open: true,
            })
        );
    };

    useEffect(() => {
        getTopAuthors();
    }, [getTopAuthors]);

    useEffect(() => {
        function handleClick() {
            setMenuOpen("");
        }

        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    return (
        <div className="top-authors-widget">
            <div className="top">
                <p>Top Authors</p>
            </div>
            <div className="body">
                <div className="controls">
                    <span onClick={() => setShowAll(!showAll)}>{showAll ? "Show Less" : "Show All"}</span>
                    <i className="fal fa-users" onClick={() => setFilterId(1)}></i>
                    <i className="fal fa-comments-alt" onClick={() => setFilterId(2)}></i>
                </div>
                <div className="authors">
                    {topAuthors
                        .sort((a, b) => (filterId === 2 ? b.ClipCount - a.ClipCount : b.FollowerCount - a.FollowerCount))
                        .slice(0, showAll ? topAuthors.length : 10)
                        .map((x, i) => {
                            return (
                                <div className={`author ${menuOpen === x.AuthorId ? "open" : ""}`} key={i}>
                                    <i className={`fab fa-${getStream(x.StreamId)}`}></i>
                                    <span className="author-name" onClick={(e) => openMenu(x.AuthorId, e)}>
                                        {x.AuthorName} <i className="fas fa-caret-down"></i>
                                    </span>
                                    <span className="author-followers">{x.FollowerCount}</span>
                                    <span className="author-clips">{x.ClipCount}</span>
                                    <div className={`author-menu`}>
                                        <p className="author-name">{x.AuthorName}</p>
                                        <p onClick={(e) => viewPosts(x.AuthorId, e)}>View Posts</p>
                                        <p onClick={() => addToInfluencerDiscovery(x.AuthorId, x.AuthorName, x.StreamId)}>Add to Influencer Discovery</p>
                                        {/* <p>Add to Author Alerts</p> */}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}

export default TopAuthors;
